import { LOCALES, INDUSTRY_PREFIX, type Locale } from "@/i18n/config";

/**
 * Paginas de industria: "como aplica esto a tu rubro", a diferencia de
 * /soluciones/* (por problema) y /casos/* (por tipo de servicio interno).
 *
 * Regla dura: cada industria existe SOLO si hay al menos un caso real
 * publicado en ese rubro (relatedCaseSlugs). Nada de contenido aspiracional
 * tipo catalogo -es exactamente lo que evita que esto se vuelva una pagina
 * generica sin sustancia detras, como advertimos al comparar con paginas de
 * la competencia que muestran decenas de "agentes" sin casos reales atras.
 *
 * Mismo patron que solutions.ts: contenido base en español, `en` como
 * override parcial opcional.
 */

export interface IndustryFaqItem {
  question: string;
  answer: string;
}

interface IndustryContent {
  /** Title para buscadores (sin la marca) cuando `title` no entra en ~60. */
  seoTitle?: string;
  /** Nombre corto, para el selector de la home y las tarjetas del indice. */
  shortLabel: string;
  /** H1: nombre de la industria. */
  title: string;
  /** Bajada para la tarjeta del indice y meta description. */
  summary: string;
  /** Meta description propia, para cuando `summary` es de una sola oracion
   * larga y recortarla (ver fitDescription en seo.ts) pierde el sentido.
   * Hasta ~158 caracteres. */
  metaDescription?: string;
  /** El problema tipico del rubro, en los terminos del cliente. */
  problem: string[];
  /** Que hace WAIA concretamente para ese rubro. */
  solution: string[];
  /** Que incluye, en bullets. */
  bullets: string[];
  /** Texto del CTA final. */
  cta: string;
  /**
   * Preguntas frecuentes del rubro: se ven en la pagina y alimentan el FAQPage
   * (ver seo.ts). Cada respuesta nombra a WAIA y sale solo de datos de este
   * repo: los casos relacionados, las FAQ de las soluciones relacionadas y las
   * respuestas sobre WAIA de la home. Sin precios, plazos ni garantias, y sin
   * "unico" o "primero": un motor de IA cita estas respuestas como hechos.
   */
  faq?: IndustryFaqItem[];
}

export interface Industry extends IndustryContent {
  slug: string;
  /** Slugs de src/data/caseStudies.ts que respaldan esta pagina. */
  relatedCaseSlugs: string[];
  /** Slugs de src/data/solutions.ts relacionados, para enlazado cruzado. */
  relatedSolutionSlugs: string[];
  en?: Partial<IndustryContent>;
  draft?: boolean;
}

export function localizeIndustry(industry: Industry, locale: Locale): Industry {
  if (locale !== "en" || !industry.en) return industry;
  return { ...industry, ...industry.en };
}

export const industries: Industry[] = [
  {
    slug: "salud",
    relatedCaseSlugs: ["emerger", "previnca"],
    relatedSolutionSlugs: ["agente-ia-whatsapp", "asistentes-virtuales-atencion"],
    shortLabel: "Salud",
    title: "Inteligencia artificial y software para salud",
    summary:
      "Videoconsultas, turnos y atención automatizada por WhatsApp para instituciones de salud que necesitan escalar sin sumar personal en cada pico de demanda.",
    problem: [
      "En salud, los picos de demanda no avisan: una temporada de invierno, un aumento estacional, y de golpe la atención presencial y telefónica no da abasto. La respuesta tradicional es sumar personal, pero eso no escala igual de rápido que la demanda.",
      "Mientras tanto, los afiliados y pacientes esperan: turnos que tardan en confirmarse, consultas que se repiten una y otra vez porque no hay un canal digital que las resuelva solo.",
    ],
    solution: [
      "Desarrollamos aplicaciones de videoconsulta que descomprimen la atención presencial en los picos de demanda, y agentes de IA por WhatsApp que gestionan turnos, información y consultas las 24 horas, integrados con la base de conocimiento real de la institución.",
      "En los dos casos, el objetivo no es reemplazar al equipo humano: es que dejen de perder tiempo en lo repetitivo y puedan dedicarse a los casos que de verdad necesitan una persona.",
    ],
    bullets: [
      "Videoconsultas para descomprimir la atención presencial y telefónica",
      "Agentes de IA por WhatsApp para turnos, información y atención 24/7",
      "Integración con sistemas de gestión existentes",
      "Arquitectura pensada para picos de demanda estacionales",
    ],
    faq: [
      {
        question: "¿Cómo puede ayudar la inteligencia artificial a una institución de salud cuando sube la demanda de atención?",
        answer:
          "Con aplicaciones de videoconsulta y agentes de IA por WhatsApp, para escalar sin sumar personal en cada pico de demanda. WAIA desarrolla aplicaciones de videoconsulta que descomprimen la atención presencial y telefónica, y agentes que gestionan turnos, información y consultas las 24 horas, integrados con la base de conocimiento real de tu institución. El objetivo no es reemplazar a tu equipo: es que se dedique a los casos que de verdad necesitan una persona.",
      },
      {
        question: "¿Qué resultados publicó WAIA en proyectos de salud?",
        answer:
          "WAIA tiene dos casos de salud publicados. En Grupo Previnca San Lorenzo, donde implementó el agente de IA María Pía por WhatsApp, el resultado publicado es -40% en el volumen de consultas al call center. En Grupo Emerger, la app de videoconsultas que desarrolló WAIA descomprimió 2 canales: la sala de atención y el teléfono, en los picos de demanda, y hoy sigue en funcionamiento.",
      },
      {
        question: "¿Qué pasa si el agente de IA de WhatsApp no puede resolver una consulta de un afiliado o paciente?",
        answer:
          "Deriva automáticamente a una persona de tu equipo, sin que quien consulta tenga que repetir lo que ya escribió. El agente de IA por WhatsApp de WAIA resuelve por sí solo lo que puede resolver y deriva apenas la consulta lo requiere; en el caso de Grupo Previnca San Lorenzo, la derivación automática va a los sectores correspondientes cuando la intervención humana resulta necesaria.",
      },
      {
        question: "¿Qué experiencia tiene WAIA en software e inteligencia artificial para salud?",
        answer:
          "WAIA tiene dos casos de salud publicados: en 2024, una app de videoconsultas para Grupo Emerger, empresa de emergencias médicas con sede en Rosario, y en 2026, el agente de IA María Pía por WhatsApp para Grupo Previnca San Lorenzo. WAIA es una empresa de software de Rosario, Argentina, especializada en inteligencia artificial: trabaja con IA desde 2021 y publica el detalle de cada proyecto.",
      },
    ],
    cta: "Quiero hablar sobre mi institución de salud",
    en: {
      shortLabel: "Healthcare",
      title: "AI and software for healthcare",
      summary:
        "Video consultations, appointments and automated WhatsApp support for healthcare institutions that need to scale without adding staff for every demand peak.",
      problem: [
        "In healthcare, demand spikes don't warn you: a winter season, a seasonal surge, and suddenly in-person and phone support can't keep up. The traditional answer is adding staff, but that doesn't scale as fast as demand does.",
        "Meanwhile, members and patients wait: appointments that take time to confirm, queries that repeat over and over because there's no digital channel that resolves them on its own.",
      ],
      solution: [
        "We build video-consultation apps that ease in-person and phone support during demand peaks, and WhatsApp AI agents that manage appointments, information and support 24 hours a day, integrated with the institution's real knowledge base.",
        "In both cases, the goal isn't to replace the human team: it's for them to stop losing time on repetitive work and focus on the cases that genuinely need a person.",
      ],
      bullets: [
        "Video consultations to ease in-person and phone support",
        "WhatsApp AI agents for appointments, information and 24/7 support",
        "Integration with existing management systems",
        "Architecture built for seasonal demand peaks",
      ],
      faq: [
        {
          question: "How can artificial intelligence help a healthcare institution when demand for care rises?",
          answer:
            "With video-consultation apps and WhatsApp AI agents, so you can scale without adding staff for every demand peak. WAIA builds video-consultation apps that ease in-person and phone support, and agents that manage appointments, information and queries 24 hours a day, integrated with your institution's real knowledge base. The goal isn't to replace your team: it's for them to focus on the cases that genuinely need a person.",
        },
        {
          question: "What results has WAIA published in healthcare projects?",
          answer:
            "WAIA has two published healthcare cases. At Grupo Previnca San Lorenzo, where it deployed the María Pía AI agent over WhatsApp, the published result is -40% in call center query volume. At Grupo Emerger, the video-consultation app WAIA built relieved 2 channels: the service room and the phone line, during demand peaks, and it is still running today.",
        },
        {
          question: "What happens if a WhatsApp AI agent can't resolve a member's or patient's query?",
          answer:
            "It automatically hands off to a person on your team, without the person asking having to repeat what they already wrote. WAIA's WhatsApp AI agent resolves on its own what it can resolve and hands off the moment a query needs it; in the Grupo Previnca San Lorenzo case, the automatic hand-off goes to the right departments whenever a human needs to step in.",
        },
        {
          question: "What experience does WAIA have in software and AI for healthcare?",
          answer:
            "WAIA has two published healthcare cases: in 2024, a video-consultation app for Grupo Emerger, a medical emergency company based in Rosario, and in 2026, the María Pía AI agent over WhatsApp for Grupo Previnca San Lorenzo. WAIA is a software company in Rosario, Argentina, specialized in artificial intelligence: it has worked with AI since 2021 and publishes the detail of every project.",
        },
      ],
      cta: "I want to talk about my healthcare institution",
    },
  },
  {
    slug: "sector-publico",
    relatedCaseSlugs: ["enapro"],
    relatedSolutionSlugs: ["asistentes-virtuales-atencion"],
    shortLabel: "Sector Público",
    seoTitle: "IA para organismos y entes públicos",
    title: "Inteligencia artificial para organismos y entes públicos",
    summary:
      "Experiencias digitales interactivas que acercan la información institucional a empresas, visitantes y ciudadanos, sin depender solo de folletería o un sitio estático.",
    problem: [
      "La comunicación institucional tradicional -folletos, cartelería, un sitio estático- no alcanza para transmitir la escala y la actividad real de un organismo grande, ni para responder las consultas puntuales de cada visitante o ciudadano.",
      "El resultado es un organismo que hace mucho pero comunica poco, porque no tiene un canal que se adapte a lo que cada persona realmente quiere saber.",
    ],
    solution: [
      "Diseñamos experiencias interactivas con IA -asistentes virtuales que responden consultas de forma conversacional- pensadas para instituciones y entes públicos que necesitan una comunicación moderna, medible y disponible en todo momento.",
      "La navegación conversacional reemplaza al folleto: en vez de mostrar lo mismo a todo el mundo, cada visitante llega a la información que realmente busca.",
    ],
    bullets: [
      "Asistente virtual para consultas sobre el organismo y su funcionamiento",
      "Experiencia optimizada para escritorio y dispositivos móviles",
      "Mayor interacción y tiempo de permanencia, medibles",
      "Posiciona al organismo como innovador en transformación digital",
    ],
    faq: [
      {
        question: "¿Cómo puede la inteligencia artificial mejorar la comunicación de un organismo público?",
        answer:
          "Con un asistente virtual con IA que responde consultas de forma conversacional, para que cada visitante llegue a la información que busca en lugar de ver lo mismo que todos. WAIA diseña estas experiencias para organismos y entes públicos que necesitan una comunicación moderna, medible y disponible en todo momento, sin depender solo de folletos, cartelería o un sitio estático.",
      },
      {
        question: "¿Qué resultados publicó WAIA en el sector público?",
        answer:
          "Los resultados que WAIA publica para este rubro son los del caso ENAPRO (Ente Administrador del Puerto de Rosario): mayor interacción de los usuarios con el contenido del puerto e incremento del tiempo de permanencia dentro de la plataforma. Según el caso, son 3 públicos -empresas, visitantes y ciudadanía- en una misma experiencia, con IA conversacional dentro del sitio institucional.",
      },
      {
        question: "¿Un asistente virtual para un organismo público es un chatbot de respuestas fijas?",
        answer:
          "No. El asistente virtual que diseña WAIA no es un chatbot de respuestas fijas: se adapta a lo que la persona realmente pregunta, no a un árbol de opciones prearmado. Responde con información oficial y actualizada de tu organización y, al ser una experiencia conversacional, podés ver qué es lo que la gente realmente pregunta y dónde se traba.",
      },
      {
        question: "¿Qué experiencia tiene WAIA con organismos y entes públicos?",
        answer:
          "WAIA tiene publicado el caso de un ente público: el Ente Administrador del Puerto de Rosario (ENAPRO), creado en 1993 por la Provincia de Santa Fe, que administra el Puerto de Rosario desde 1994. WAIA desarrolló para ENAPRO una experiencia interactiva con IA y un asistente virtual que responde consultas sobre el puerto y su funcionamiento. WAIA es una empresa de software de Rosario, Argentina, que trabaja con inteligencia artificial desde 2021.",
      },
    ],
    cta: "Quiero modernizar la comunicación de mi organismo",
    en: {
      shortLabel: "Public Sector",
      title: "AI for government bodies and public agencies",
      summary:
        "Interactive digital experiences that bring institutional information closer to businesses, visitors and citizens, without relying only on brochures or a static site.",
      problem: [
        "Traditional institutional communication -brochures, signage, a static site- isn't enough to convey the real scale and activity of a large agency, or to answer each visitor's or citizen's specific questions.",
        "The result is an agency that does a lot but communicates little, because it has no channel that adapts to what each person actually wants to know.",
      ],
      solution: [
        "We design AI-driven interactive experiences -virtual assistants that answer queries conversationally- built for institutions and public agencies that need modern, measurable communication available at all times.",
        "Conversational navigation replaces the brochure: instead of showing the same thing to everyone, each visitor reaches the information they're actually looking for.",
      ],
      bullets: [
        "Virtual assistant for queries about the agency and how it operates",
        "Experience optimised for desktop and mobile devices",
        "Higher engagement and time spent, both measurable",
        "Positions the agency as an innovator in digital transformation",
      ],
      faq: [
        {
          question: "How can artificial intelligence improve a public agency's communication?",
          answer:
            "With an AI virtual assistant that answers queries conversationally, so each visitor reaches the information they're looking for instead of seeing the same thing as everyone else. WAIA designs these experiences for government bodies and public agencies that need modern, measurable communication available at all times, without relying only on brochures, signage or a static site.",
        },
        {
          question: "What results has WAIA published in the public sector?",
          answer:
            "The results WAIA publishes for this industry are those of the ENAPRO case (Ente Administrador del Puerto de Rosario): higher user engagement with the port's content and longer time spent on the platform. According to the case, it is 3 audiences -companies, visitors and citizens- in one experience, with conversational AI inside the institutional site.",
        },
        {
          question: "Is a virtual assistant for a public agency a fixed-response chatbot?",
          answer:
            "No. The virtual assistant WAIA designs is not a fixed-response chatbot: it adapts to what the person actually asks, not a pre-built decision tree. It answers with official, up-to-date information from your organisation and, being a conversational experience, you can see what people actually ask and where they get stuck.",
        },
        {
          question: "What experience does WAIA have with government bodies and public agencies?",
          answer:
            "WAIA has published the case of a public body: the Ente Administrador del Puerto de Rosario (ENAPRO), created in 1993 by the Province of Santa Fe, which has run the Port of Rosario since 1994. WAIA built for ENAPRO an interactive AI experience and a virtual assistant that answers questions about the port and how it operates. WAIA is a software company in Rosario, Argentina, that has worked with artificial intelligence since 2021.",
        },
      ],
      cta: "I want to modernise my agency's communication",
    },
  },
  {
    slug: "retail",
    relatedCaseSlugs: ["verona"],
    relatedSolutionSlugs: ["ecommerce-b2b-mayorista"],
    shortLabel: "Retail",
    title: "E-commerce y digitalización para retail",
    summary:
      "Canales digitales para comercios donde la venta online no era la prioridad, combinando e-commerce con la atención en sucursal en vez de reemplazarla.",
    problem: [
      "En muchos rubros de retail, la digitalización no era una prioridad: los clientes resuelven casi todo en el mostrador de la sucursal, incluso consultas que podrían resolverse online en minutos.",
      "El riesgo de subestimar esto es quedar atrás cuando el rubro sí empieza a moverse online, sin la experiencia ni la infraestructura para competir.",
    ],
    solution: [
      "Desarrollamos soluciones 360 que combinan e-commerce con la gestión de turnos en sucursal mediante tótems interactivos, incorporando la virtualidad como una alternativa real de compra sin abandonar lo presencial.",
      "El canal digital no compite con la sucursal: la complementa, resolviendo online lo que antes solo se podía resolver en persona.",
    ],
    bullets: [
      "E-commerce integrado con la operación de sucursal",
      "Gestión de turnos mediante tótems interactivos",
      "Canales digitales pensados para un público que recién empieza a comprar online",
      "Solución 360, no solo una tienda online aislada",
    ],
    faq: [
      {
        question: "¿Cómo puede un comercio minorista sumar venta online sin dejar de atender en sucursal?",
        answer:
          "Con una solución 360 que combina e-commerce y gestión de turnos en sucursal, y no solo una tienda online aislada. WAIA la desarrolla con tótems interactivos para los turnos: el canal digital complementa a tu sucursal y resuelve online lo que antes solo se podía resolver en persona, sin abandonar lo presencial.",
      },
      {
        question: "¿Qué resultados logró un comercio de retail al digitalizar su venta con WAIA?",
        answer:
          "WAIA publicó el caso de Neumáticos Verona, donde la compra online pasó a ser una alternativa real y el 25% de los carritos abandonados se recuperan con el bot de WhatsApp. Es una solución 360 que combinó e-commerce y gestión de turnos en sucursal con tótems interactivos, y el bot recontacta automáticamente a quien deja un carrito abandonado.",
      },
      {
        question: "¿Cómo se evita que el stock se descuadre entre canales de venta?",
        answer:
          "Con integraciones en tiempo real de stock, medios de pago y envíos entre canales, para que el stock no se descuadre entre mayorista y minorista. WAIA las implementa sobre plataformas multitienda cuando tu negocio lo necesita y trabaja con integraciones a medida cuando la plataforma estándar no alcanza.",
      },
      {
        question: "¿Qué experiencia tiene WAIA en retail?",
        answer:
          "WAIA tiene publicado en retail el caso de Neumáticos Verona, un comercio de neumáticos con venta online y atención en sucursal, donde una solución 360 combinó e-commerce y gestión de turnos con tótems interactivos. WAIA es una empresa de software de Rosario, Argentina, especializada en inteligencia artificial, que desarrolla software a medida, tiendas de e-commerce e IA aplicada a negocios reales.",
      },
    ],
    cta: "Quiero digitalizar mi comercio",
    en: {
      shortLabel: "Retail",
      title: "E-commerce and digitalisation for retail",
      summary:
        "Digital channels for retailers where online sales weren't a priority, combining e-commerce with in-store service instead of replacing it.",
      problem: [
        "In many retail industries, digitalisation wasn't a priority: customers resolve almost everything at the branch counter, even queries that could be solved online in minutes.",
        "The risk of underestimating this is falling behind once the industry does start moving online, without the experience or infrastructure to compete.",
      ],
      solution: [
        "We build 360 solutions combining e-commerce with in-store queue management through interactive kiosks, bringing in digital as a real buying alternative without abandoning the in-person side.",
        "The digital channel doesn't compete with the branch: it complements it, solving online what could previously only be resolved in person.",
      ],
      bullets: [
        "E-commerce integrated with branch operations",
        "Queue management through interactive kiosks",
        "Digital channels built for an audience just starting to buy online",
        "A 360 solution, not just an isolated online store",
      ],
      faq: [
        {
          question: "How can a retailer add online sales without giving up in-store service?",
          answer:
            "With a 360 solution that combines e-commerce with in-store queue management, not just an isolated online store. WAIA builds it with interactive kiosks for queue management: the digital channel complements your branch and solves online what could previously only be resolved in person, without abandoning the in-person side.",
        },
        {
          question: "What results did a retailer get from digitalising its sales with WAIA?",
          answer:
            "WAIA published the Neumáticos Verona case, where buying online became a real alternative and 25% of abandoned carts are recovered by the WhatsApp bot. It's a 360 solution that combined e-commerce with in-store queue management through interactive kiosks, and the bot automatically follows up on anyone who leaves an abandoned cart.",
        },
        {
          question: "How do you keep stock from falling out of sync across sales channels?",
          answer:
            "With real-time integrations for stock, payments and shipping across channels, so stock doesn't fall out of sync between wholesale and retail. WAIA builds them on multi-store platforms when your business needs it and also works with custom integrations when the standard platform isn't enough.",
        },
        {
          question: "What experience does WAIA have in retail?",
          answer:
            "WAIA has a published retail case, Neumáticos Verona, a tyre retailer selling online with in-branch service, where a 360 solution combined e-commerce with queue management through interactive kiosks. WAIA is a software company in Rosario, Argentina, specialized in artificial intelligence, that builds custom software, e-commerce stores and AI applied to real businesses.",
        },
      ],
      cta: "I want to digitalise my store",
    },
  },
  {
    slug: "ecommerce",
    relatedCaseSlugs: ["sonder"],
    relatedSolutionSlugs: ["ecommerce-b2b-mayorista"],
    shortLabel: "E-commerce",
    title: "E-commerce mayorista y minorista",
    summary:
      "Canales de venta online que escalan sin romperse: integraciones en tiempo real de stock, pagos y envíos para negocios que atienden mayorista y minorista a la vez.",
    metaDescription:
      "Canales de venta online que escalan sin romperse: stock, pagos y envíos integrados en tiempo real para negocios mayoristas y minoristas a la vez.",
    problem: [
      "A medida que crece el volumen de pedidos, conciliar stock, pagos y envíos a mano entre canales se vuelve un cuello de botella. El proceso manual que funcionaba con poco volumen deja de alcanzar apenas el negocio escala.",
      "Vender mayorista y minorista al mismo tiempo agrava el problema: cada canal tiene sus propias reglas de precio, y sin una arquitectura pensada para eso, el catálogo termina duplicado o desincronizado.",
    ],
    solution: [
      "Implementamos integraciones en tiempo real de stock, medios de pago y envíos, y desarrollamos soluciones multitienda que permiten una gestión más ágil y centralizada del negocio online.",
      "Sumamos funcionalidades específicas del rubro cuando agregan valor real -como un probador virtual con inteligencia artificial- en vez de sumar tecnología porque sí.",
    ],
    bullets: [
      "Integraciones en tiempo real de stock, pagos y envíos",
      "Soluciones multitienda para mayorista y minorista",
      "Funcionalidades de IA cuando resuelven un problema concreto del negocio",
      "Arquitectura pensada para escalar el volumen de pedidos",
    ],
    faq: [
      {
        question: "¿Cómo integrar stock, pagos y envíos en un e-commerce que vende mayorista y minorista?",
        answer:
          "Con integraciones en tiempo real de stock, medios de pago y envíos entre canales, y con soluciones multitienda que centralizan la gestión. WAIA las implementa. Conciliar a mano stock, pagos y envíos entre canales se vuelve un cuello de botella cuando crece el volumen de pedidos. Sumamos IA, como un probador virtual, solo cuando resuelve un problema concreto del negocio.",
      },
      {
        question: "¿Qué resultados publicó WAIA en e-commerce mayorista y minorista?",
        answer:
          "En e-commerce mayorista y minorista, WAIA publicó los resultados del cliente Sonder: +50% de optimización de ganancias en ventas y +30% de ahorro en pasarelas de pago. Para Sonder entregamos integraciones en tiempo real de stock, medios de pago y envíos, una solución multitienda y un probador virtual basado en inteligencia artificial.",
      },
      {
        question: "¿Se puede vender mayorista y minorista en la misma tienda sin que el stock se descuadre?",
        answer:
          "Sí, con precios y condiciones diferenciadas según el tipo de cliente y sin duplicar el catálogo. WAIA integra stock, medios de pago y envíos en tiempo real para que el stock no se descuadre entre mayorista y minorista. Si tu negocio lo necesita, implementamos soluciones multitienda para centralizar la gestión: qué estructura conviene depende de tu negocio.",
      },
      {
        question: "¿Qué experiencia tiene WAIA en e-commerce mayorista y minorista?",
        answer:
          "WAIA tiene experiencia publicada en e-commerce mayorista y minorista: el caso de Sonder, marca rosarina de indumentaria deportiva que vende online y en más de 300 casas de deportes y locales multimarca. El e-commerce es una de nuestras tres líneas de servicio, somos partner oficial de OpenCart en Argentina y hacemos integraciones a medida cuando la plataforma estándar no alcanza.",
      },
    ],
    cta: "Quiero escalar mi e-commerce",
    en: {
      shortLabel: "E-commerce",
      title: "Wholesale and retail e-commerce",
      summary:
        "Online sales channels that scale without breaking: real-time stock, payment and shipping integrations for businesses serving wholesale and retail at once.",
      problem: [
        "As order volume grows, manually reconciling stock, payments and shipping across channels becomes a bottleneck. The manual process that worked at low volume stops being enough as soon as the business scales.",
        "Selling wholesale and retail at the same time makes it worse: each channel has its own pricing rules, and without an architecture built for that, the catalogue ends up duplicated or out of sync.",
      ],
      solution: [
        "We build real-time integrations for stock, payments and shipping, and multi-store solutions for faster, more centralised management of the online business.",
        "We add industry-specific features when they add real value -like an AI-powered virtual try-on- instead of adding technology for its own sake.",
      ],
      bullets: [
        "Real-time stock, payment and shipping integrations",
        "Multi-store solutions for wholesale and retail",
        "AI features when they solve a concrete business problem",
        "Architecture built to scale order volume",
      ],
      faq: [
        {
          question: "How do you integrate stock, payments and shipping in an e-commerce business that sells wholesale and retail?",
          answer:
            "With real-time integrations for stock, payments and shipping across channels, and with multi-store solutions that centralise management. WAIA builds them. Manually reconciling stock, payments and shipping across channels becomes a bottleneck as order volume grows. We add AI, such as a virtual try-on, only when it solves a concrete business problem.",
        },
        {
          question: "What results has WAIA published in wholesale and retail e-commerce?",
          answer:
            "In wholesale and retail e-commerce, WAIA published the results of the client Sonder: +50% increase in sales profitability and +30% savings on payment gateway fees. For Sonder we delivered real-time integrations for stock, payments and shipping, a multi-store solution and an AI-powered virtual try-on.",
        },
        {
          question: "Can I sell wholesale and retail from the same store without stock falling out of sync?",
          answer:
            "Yes, with different prices and terms depending on the customer type, without duplicating your catalogue. WAIA integrates stock, payments and shipping in real time so stock doesn't fall out of sync between wholesale and retail. If your business needs it, we build multi-store solutions to centralise management: which structure fits depends on your business.",
        },
        {
          question: "What experience does WAIA have in wholesale and retail e-commerce?",
          answer:
            "WAIA has published experience in wholesale and retail e-commerce: the Sonder case, a Rosario sportswear brand that sells online and through more than 300 sports stores and multi-brand retailers. E-commerce is one of our three service lines, we're an official OpenCart partner in Argentina, and we do custom integrations when the standard platform isn't enough.",
        },
      ],
      cta: "I want to scale my e-commerce",
    },
  },
  {
    slug: "hoteleria",
    relatedCaseSlugs: ["marriott"],
    relatedSolutionSlugs: ["desarrollo-software-a-medida"],
    shortLabel: "Hotelería",
    title: "Campañas digitales y desarrollo para hotelería",
    summary:
      "Landing pages y sistemas integrados para programas de fidelización y campañas regionales, cuando las páginas genéricas de la marca no alcanzan para captar a un usuario local.",
    problem: [
      "Las páginas genéricas de una marca global no están pensadas para capturar a un usuario regional con una oferta puntual, y eso limita la conversión de las campañas locales.",
      "Sin páginas propias integradas a los sistemas de la marca, cada campaña regional pierde alcance frente a lo que podría captar con una experiencia pensada para esa audiencia específica.",
    ],
    solution: [
      "Creamos landing pages integradas a los sistemas de la marca, pensadas para captar usuarios de una región específica y potenciar programas de fidelización.",
      "El resultado no es una página más: es una pieza conectada con la infraestructura existente para que la campaña realmente convierta, en vez de quedar aislada del resto del ecosistema digital de la marca.",
    ],
    bullets: [
      "Landing pages regionales integradas a los sistemas de la marca",
      "Pensadas para potenciar programas de fidelización",
      "Mayor captación de usuarios en las zonas de la campaña",
      "Integración con la infraestructura digital existente, no una pieza aislada",
    ],
    faq: [
      {
        question: "¿Cómo capto usuarios de una región con campañas digitales si las páginas genéricas de mi marca hotelera no alcanzan?",
        answer:
          "Con landing pages propias, integradas a los sistemas de tu marca y pensadas para captar usuarios de una región con una oferta puntual. Las páginas genéricas de una marca global no están pensadas para capturar a un usuario regional con una oferta puntual, y eso limita la conversión de las campañas locales. WAIA crea landing pages conectadas con tu infraestructura digital existente para que la campaña no quede aislada del resto del ecosistema digital de tu marca.",
      },
      {
        question: "¿Qué resultados publicó WAIA en proyectos de hotelería?",
        answer:
          "WAIA publicó un caso en hotelería: Marriott Bonvoy. El resultado publicado es que más usuarios fueron captados para su programa de fidelización en el Caribe y Latinoamérica, con landing pages integradas a sus sistemas. Las zonas donde se potenció la captación son Caribe y LatAm. Marriott Bonvoy reúne más de 270 millones de miembros (2025) y 31 marcas.",
      },
      {
        question: "¿Las landing pages se integran con los sistemas que ya usa mi marca?",
        answer:
          "Sí. WAIA integra lo que desarrolla con los sistemas que ya tenés, mediante integraciones a medida. En hotelería, las landing pages de Marriott Bonvoy se construyeron integradas a sus sistemas. WAIA también diseña la arquitectura pensando en escalar, no solo en el lanzamiento inicial, y queda disponible después de la entrega: no desaparece cuando el proyecto sale a producción.",
      },
      {
        question: "¿Qué experiencia tiene WAIA en hotelería?",
        answer:
          "WAIA tiene un caso publicado en hotelería: las landing pages integradas a los sistemas de Marriott Bonvoy, para campañas regionales puntuales en el Caribe y Latinoamérica. WAIA es una empresa de software de Rosario, Argentina, con casos publicados y verificables.",
      },
    ],
    cta: "Quiero potenciar mis campañas digitales",
    en: {
      shortLabel: "Hospitality",
      title: "Digital campaigns and development for hospitality",
      summary:
        "Landing pages and integrated systems for loyalty programmes and regional campaigns, for when a brand's generic pages aren't enough to capture a local user.",
      problem: [
        "A global brand's generic pages aren't built to capture a regional user with a specific offer, which limits how well local campaigns convert.",
        "Without dedicated pages integrated with the brand's systems, every regional campaign loses reach compared to what an experience built for that specific audience could capture.",
      ],
      solution: [
        "We build landing pages integrated with the brand's systems, designed to capture users from a specific region and boost loyalty programmes.",
        "The result isn't just another page: it's a piece connected to the existing infrastructure so the campaign actually converts, instead of sitting isolated from the rest of the brand's digital ecosystem.",
      ],
      bullets: [
        "Regional landing pages integrated with the brand's systems",
        "Built to boost loyalty programmes",
        "Higher user capture across the campaign's target regions",
        "Integrated with existing digital infrastructure, not an isolated piece",
      ],
      faq: [
        {
          question: "How do I capture users from a region with digital campaigns when my hotel brand's generic pages aren't enough?",
          answer:
            "With dedicated landing pages, integrated with your brand's systems and built to capture users from a region with a specific offer. A global brand's generic pages aren't built to capture a regional user with a specific offer, which limits how well local campaigns convert. WAIA builds landing pages connected to your existing digital infrastructure so the campaign doesn't sit isolated from the rest of your brand's digital ecosystem.",
        },
        {
          question: "What results has WAIA published in hospitality projects?",
          answer:
            "WAIA published a case in hospitality: Marriott Bonvoy. The published result is that more users were captured for its loyalty programme across the Caribbean and Latin America, with landing pages integrated with its systems. The regions where sign-ups were boosted are Caribbean and LatAm. Marriott Bonvoy has more than 270 million members (2025) and spans 31 brands.",
        },
        {
          question: "Do the landing pages integrate with the systems my brand already uses?",
          answer:
            "Yes. WAIA integrates what it builds with the systems you already have, through custom integrations. In hospitality, the Marriott Bonvoy landing pages were built integrated with its systems. WAIA also designs the architecture with scaling in mind, not just the initial launch, and stays available after delivery: it doesn't disappear once the project goes live.",
        },
        {
          question: "What experience does WAIA have in hospitality?",
          answer:
            "WAIA has a published case in hospitality: the landing pages integrated with Marriott Bonvoy's systems, for specific regional campaigns across the Caribbean and Latin America. WAIA is a software company in Rosario, Argentina, with published, verifiable case studies.",
        },
      ],
      cta: "I want to boost my digital campaigns",
    },
  },
  {
    slug: "industrial",
    relatedCaseSlugs: ["crucijuegos"],
    relatedSolutionSlugs: ["configurador-de-productos", "desarrollo-software-a-medida"],
    shortLabel: "Industrial",
    title: "Software e IA para industria y manufactura",
    summary:
      "Configuradores inteligentes que automatizan cotizaciones de productos técnicos personalizados, para que el equipo comercial no dependa de ingeniería en cada venta.",
    problem: [
      "Cuando el producto se arma a medida -maquinaria, equipamiento, mobiliario industrial-, cada venta empieza revisando planos, validando medidas y calculando materiales antes de poder cotizar. Ese proceso depende todo el tiempo del equipo técnico.",
      "El resultado: propuestas que tardan días en salir, un equipo comercial que no puede responder rápido a nuevos clientes, y errores de configuración que aparecen recién cuando ya es tarde para corregirlos sin fricción.",
    ],
    solution: [
      "Diseñamos configuradores web que traducen las reglas técnicas del producto en un motor de validación automática, integrados con la información técnica existente -por ejemplo, en AutoCAD-, para que el equipo comercial arme una propuesta válida sin depender de ingeniería en cada paso.",
      "También construimos el resto del software a medida que una operación industrial necesita: desde la gestión interna hasta las integraciones entre sistemas existentes.",
    ],
    bullets: [
      "Configuradores de productos personalizados (CPQ)",
      "Integración con diseños técnicos existentes",
      "Validación automática de reglas de negocio en tiempo real",
      "Software a medida para el resto de la operación",
    ],
    faq: [
      {
        question: "¿Cómo se automatizan las cotizaciones de productos industriales hechos a medida?",
        answer:
          "Con un configurador web de productos (CPQ) que traduce las reglas técnicas del producto en un motor de validación automática. WAIA diseña estos configuradores para que tu equipo comercial arme una propuesta válida sin depender de ingeniería en cada paso, y para que las reglas de negocio se validen en tiempo real en lugar de descubrir los errores de configuración cuando ya es tarde para corregirlos sin fricción.",
      },
      {
        question: "¿Qué resultados publicó WAIA en software para industria y manufactura?",
        answer:
          "WAIA publicó el caso de Crucijuegos, fabricante de parques infantiles personalizados: con un configurador web de cotizaciones, el resultado fue +50% más rápido para entregar una propuesta comercial. Además, su equipo comercial ganó autonomía para configurar propuestas sin depender de ingeniería en cada paso, con menos errores por configuraciones manuales, validadas ahora en tiempo real, y más propuestas entregadas a tiempo se tradujeron en más cotizaciones cerradas.",
      },
      {
        question: "¿Puedo usar un configurador de cotizaciones si mis diseños técnicos ya están en AutoCAD?",
        answer:
          "Sí, WAIA integra la información técnica que ya tenés en AutoCAD u otro sistema de diseño directamente al configurador, sin cambiar el flujo de trabajo de tu equipo técnico ni duplicar su trabajo. En el caso de Crucijuegos, la integración con los diseños técnicos en AutoCAD se hizo sin modificar el flujo de trabajo del equipo de ingeniería.",
      },
      {
        question: "¿Qué experiencia tiene WAIA en software para la industria?",
        answer:
          "WAIA tiene publicado el caso de Crucijuegos en software para industria y manufactura: una empresa rosarina fundada en 1992 que diseña y fabrica juegos de plaza y mobiliario urbano, para la que diseñó un configurador web de cotizaciones integrado con AutoCAD. WAIA es una empresa de software de Rosario, Argentina, que desarrolla software a medida, tiendas de e-commerce e IA aplicada a negocios reales, con casos publicados y verificables.",
      },
    ],
    cta: "Quiero automatizar cotizaciones en mi empresa",
    en: {
      shortLabel: "Manufacturing",
      title: "Software and AI for industry and manufacturing",
      summary:
        "Smart configurators that automate quoting for custom technical products, so the sales team doesn't depend on engineering for every sale.",
      problem: [
        "When a product is built to order -machinery, equipment, industrial furniture-, every sale starts by reviewing drawings, validating measurements and calculating materials before a quote can even go out. That process constantly depends on the technical team.",
        "The result: proposals that take days to go out, a sales team that can't respond quickly to new clients, and configuration errors that only show up once it's too late to fix them without friction.",
      ],
      solution: [
        "We design web configurators that translate a product's technical rules into an automatic validation engine, integrated with existing technical data -in AutoCAD, for example- so the sales team can put together a valid proposal without depending on engineering at every step.",
        "We also build the rest of the custom software an industrial operation needs: from internal management to integrations between existing systems.",
      ],
      bullets: [
        "Custom product configurators (CPQ)",
        "Integration with existing technical designs",
        "Automatic real-time business rule validation",
        "Custom software for the rest of the operation",
      ],
      faq: [
        {
          question: "How do you automate quotes for industrial products built to order?",
          answer:
            "With a web product configurator (CPQ) that translates the product's technical rules into an automatic validation engine. WAIA designs these configurators so your sales team can put together a valid proposal without depending on engineering at every step, and so business rules are validated in real time instead of configuration errors showing up when it's already too late to fix them without friction.",
        },
        {
          question: "What results has WAIA published in software for industry and manufacturing?",
          answer:
            "WAIA published the Crucijuegos case, a manufacturer of custom playgrounds: with a web quoting configurator, the result was +50% faster to deliver a commercial proposal. In addition, its sales team gained autonomy to configure proposals without depending on engineering at every step, with fewer errors from manual configuration, now validated in real time, and more proposals delivered on time meant more quotes closed.",
        },
        {
          question: "Can I use a quoting configurator if my technical designs are already in AutoCAD?",
          answer:
            "Yes, WAIA integrates the technical data you already have in AutoCAD or another design system directly into the configurator, without changing your technical team's workflow or duplicating its work. In the Crucijuegos case, the integration with the technical designs in AutoCAD was done without changing the engineering team's workflow.",
        },
        {
          question: "What experience does WAIA have in software for industry?",
          answer:
            "WAIA has published the Crucijuegos case in software for industry and manufacturing: a Rosario company founded in 1992 that designs and manufactures playground equipment and street furniture, for which it designed a web quoting configurator integrated with AutoCAD. WAIA is a software company in Rosario, Argentina, that builds custom software, e-commerce stores and AI applied to real businesses, with published, verifiable case studies.",
        },
      ],
      cta: "I want to automate quoting at my company",
    },
  },
  {
    slug: "banca-seguros",
    relatedCaseSlugs: ["riskrator", "sancristobal", "boomid"],
    relatedSolutionSlugs: [
      "desarrollo-software-a-medida",
      "automatizacion-procesos-ia",
      "infraestructura-cloud-devops",
      "iso-27001-soc-2",
    ],
    shortLabel: "Banca y Seguros",
    title: "Software e infraestructura para banca y seguros",
    summary:
      "Automatización de procesos críticos e infraestructura cloud de alta disponibilidad para instituciones financieras y de identidad que no pueden permitirse errores ni caídas.",
    metaDescription:
      "Automatización de procesos críticos e infraestructura cloud de alta disponibilidad para instituciones financieras y de identidad. Mirá los casos reales →",
    problem: [
      "En banca y seguros, un proceso manual no es solo lento: es un riesgo. Cada assessment de riesgo, cada validación de identidad, cada alta de cliente que depende de una persona revisando planillas es una demora que se traduce en clientes que esperan y en un equipo que no escala.",
      "Al mismo tiempo, la infraestructura que sostiene todo esto no puede fallar: una caída o una brecha de seguridad en una plataforma financiera tiene un costo que no tiene ningún otro rubro.",
    ],
    solution: [
      "Automatizamos los procesos manuales más sensibles -como el assessment de riesgo de nuevos clientes- para que dejen de depender de la disponibilidad de una persona, sin perder el control ni la trazabilidad que exige el rubro.",
      "Y del otro lado, diseñamos y operamos la infraestructura cloud que sostiene estas plataformas: alta disponibilidad real, auto-escalado, monitoreo activo y un pipeline de despliegue confiable, para que la promesa de disponibilidad se cumpla en producción, no solo en el papel.",
    ],
    bullets: [
      "Automatización de assessments y procesos de onboarding",
      "Infraestructura cloud de alta disponibilidad (multi-zona, auto-escalado)",
      "Monitoreo activo con alertas en tiempo real",
      "Pipelines de CI/CD y hardening de seguridad continuo",
    ],
    faq: [
      {
        question: "¿Cómo se automatiza el assessment de riesgo y el alta de clientes en un banco o una aseguradora?",
        answer:
          "Se automatiza el proceso manual para que deje de depender de la disponibilidad de una persona revisando planillas, sin perder el control ni la trazabilidad que exige el rubro. WAIA automatiza assessments y procesos de onboarding, como el assessment de riesgo de nuevos clientes, y diseña y opera la infraestructura cloud que sostiene la plataforma: alta disponibilidad, auto-escalado, monitoreo activo y un pipeline de despliegue confiable.",
      },
      {
        question: "¿Qué resultados publicó WAIA en proyectos para instituciones financieras y de identidad?",
        answer:
          "WAIA publicó resultados con RiskRator y BoomID. En RiskRator, plataforma de evaluación de riesgo financiero usada por bancos, el alta de un cliente nuevo bajó de una semana a 2 horas en la primera etapa y, con las siguientes iteraciones, a un promedio de 30 minutos. En BoomID, plataforma de identidad biométrica, la infraestructura AWS que WAIA diseñó y opera tiene 99,9% de disponibilidad validada en producción.",
      },
      {
        question: "¿Cómo compruebo que la infraestructura de mi plataforma financiera realmente está disponible?",
        answer:
          "Con monitoreo activo y alarmas sobre el estado real de cada servicio (CloudWatch), no solo con una promesa de SLA en el papel. WAIA diseña y opera infraestructura AWS en múltiples zonas de disponibilidad, con alertas antes de que un problema llegue a impactar a un usuario. En BoomID, la redundancia entre zonas es real: la caída de una no afecta el servicio.",
      },
      {
        question: "¿Qué experiencia tiene WAIA en banca y seguros?",
        answer:
          "WAIA es una empresa de software de Rosario, Argentina, con tres casos publicados y verificables relacionados con banca y seguros. Automatizó el assessment de riesgo de RiskRator, plataforma usada por bancos; diseñó y opera la infraestructura AWS de BoomID, plataforma de identidad biométrica; y sostuvo durante aproximadamente un año un equipo de desarrollo dedicado para Grupo San Cristóbal, grupo asegurador, incluida su operación en Uruguay.",
      },
    ],
    cta: "Quiero hablar sobre mi institución financiera",
    en: {
      shortLabel: "Banking & Insurance",
      title: "Software and infrastructure for banking and insurance",
      summary:
        "Automating critical processes and running high-availability cloud infrastructure for financial and identity institutions that can't afford mistakes or downtime.",
      metaDescription:
        "Critical process automation and high-availability cloud infrastructure for financial and identity institutions. See the real cases behind it →",
      problem: [
        "In banking and insurance, a manual process isn't just slow — it's a risk. Every risk assessment, every identity check, every customer onboarding that depends on someone reviewing spreadsheets is a delay that means customers waiting and a team that can't scale.",
        "At the same time, the infrastructure behind all of it can't fail: downtime or a security gap in a financial platform costs more than it would in almost any other industry.",
      ],
      solution: [
        "We automate the most sensitive manual processes -like new customer risk assessments- so they stop depending on one person's availability, without losing the control or traceability the industry demands.",
        "And on the other side, we design and run the cloud infrastructure behind these platforms: real high availability, auto-scaling, active monitoring and a reliable deployment pipeline, so the uptime promise holds in production, not just on paper.",
      ],
      bullets: [
        "Assessment and onboarding process automation",
        "High-availability cloud infrastructure (multi-zone, auto-scaling)",
        "Active monitoring with real-time alerts",
        "CI/CD pipelines and ongoing security hardening",
      ],
      faq: [
        {
          question: "How do you automate risk assessment and customer onboarding at a bank or an insurer?",
          answer:
            "You automate the manual process so it stops depending on one person's availability to review spreadsheets, without losing the control or traceability the industry demands. WAIA automates assessments and onboarding processes, like new customer risk assessments, and designs and runs the cloud infrastructure behind the platform: high availability, auto-scaling, active monitoring and a reliable deployment pipeline.",
        },
        {
          question: "What results has WAIA published in projects for financial and identity institutions?",
          answer:
            "WAIA published results with RiskRator and BoomID. At RiskRator, a financial risk assessment platform used by banks, onboarding a new customer dropped from a week to 2 hours in the first stage and, with later iterations, to an average of 30 minutes. At BoomID, a biometric identity platform, the AWS infrastructure WAIA designed and runs has 99.9% availability validated in production.",
        },
        {
          question: "How do I check that my financial platform's infrastructure is actually available?",
          answer:
            "With active monitoring and alarms on the real state of each service (CloudWatch), not just an SLA promise on paper. WAIA designs and runs AWS infrastructure across multiple availability zones, with alerts before an issue reaches a user. At BoomID, redundancy across zones is real: one going down doesn't affect the service.",
        },
        {
          question: "What experience does WAIA have in banking and insurance?",
          answer:
            "WAIA is a software company in Rosario, Argentina, with three published, verifiable cases related to banking and insurance. It automated the risk assessment for RiskRator, a platform used by banks; designed and runs the AWS infrastructure for BoomID, a biometric identity platform; and kept a dedicated development team working for about a year for Grupo San Cristóbal, an insurance group, including its Uruguay operation.",
        },
      ],
      cta: "I want to talk about my financial institution",
    },
  },
];

export const publishedIndustries = industries.filter((i) => !i.draft);

export function getIndustry(slug: string): Industry | undefined {
  return publishedIndustries.find((i) => i.slug === slug);
}

/** Rutas de detalle a pre-renderizar: cada industria publicada, en los dos idiomas. */
export const industryRoutes: string[] = publishedIndustries.flatMap((i) =>
  LOCALES.map((locale) => `${INDUSTRY_PREFIX[locale]}/${i.slug}`),
);
